/**
 * Editor singleton state — the shared `state` + `els` objects + bbox cache.
 *
 * Extracted from editor.ts in Phase EB.1 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §7).
 *
 * Rationale: `state` and `els` are module-scope singletons referenced by every
 * cluster in the editor. Owning them in a dedicated module gives all
 * subsequent extraction phases a clean `import { state, els } from
 * './editor-state.js'` contract — analogous to how `editor-lifecycle-state.ts`,
 * `oplog.ts`, and `save-flow.ts` are already cleanly separated.
 *
 * The `_bboxCache` singleton lives here too because it is referenced by both
 * Cluster D (render: applyZoom invalidation site) and Cluster B (empty-state:
 * resetEditorState invalidation site) — promoting it to the shared singleton
 * module avoids a cross-cluster dependency on bbox-cache.ts.
 *
 * Per the "Type-in-place before decomposing" rule (CLAUDE.md): `StateShape`
 * is already strict-clean; this extraction preserves the existing `any`
 * annotations on the still-untyped `.js` boundaries (interop, drag-move,
 * etc.) — tightening them is the `ts-strict-zero` Epic's job.
 */
import { generateSessionId } from "./idb-oplog.js";
import { OpLog } from "./oplog.js";
import { computeSelectionState } from "./editor-state-logic.js";
// Phase FB.5 (frontend-mv-architecture-260524.md): PendingInsertStore
// relocated from controllers/insert-flow.js to store/pending-insert.ts —
// the class IS state (in-memory Map with subscribe semantics), so the
// store layer is its canonical home. This breaks the store→controllers
// layer violation flagged by tools/lint/lint-frontend-layer-imports.py.
import { PendingInsertStore } from "./store/pending-insert.js";
import { EditorLifecycleState, EditorStateMachine, } from "./editor-lifecycle-state.js";
import { getRequired, querySelectorRequired } from "./dom-utils.js";
import { createBboxCache } from "./bbox-cache.js";
export const els = {
    emptyState: getRequired("editor-empty-state"),
    emptyCard: querySelectorRequired(".editor-empty-card"),
    workspace: getRequired("editor-workspace"),
    fileInput: getRequired("editor-file-input"),
    browseLink: getRequired("editor-browse-link"),
    fileName: getRequired("editor-file-name"),
    canvas: getRequired("editor-pdf-canvas"),
    pageframe: getRequired("editor-pdf-pageframe"),
    overlay: getRequired("editor-pdf-overlay"),
    // multi-page scroll container (the primary render surface; the single-
    // page pageframe above is a legacy alias).
    pdfPages: getRequired("editor-pdf-pages"),
    overlaysToggle: getRequired("editor-overlays-toggle"),
    mcidToggle: getRequired("editor-mcid-toggle"),
    pdfScroll: getRequired("editor-pdf-scroll"),
    // Screen reader toggle button in cards-pane toolbar.
    srToggleBtn: getRequired("editor-sr-toggle-btn"),
    cardsPaneTitle: getRequired("editor-cards-pane-title"),
    pageIndicator: getRequired("editor-page-indicator"),
    structList: getRequired("editor-struct-list"),
    treeCount: getRequired("editor-tree-count"),
    metaTitle: getRequired("editor-meta-title"),
    metaLang: getRequired("editor-meta-lang"),
    metaFormat: getRequired("editor-meta-format"),
    // Two-pane layout elements.
    paneEditor: getRequired("editor-pane-editor"),
    panePreview: getRequired("editor-pane-preview"),
    paneInspector: getRequired("editor-pane-inspector"),
    statusPage: getRequired("editor-status-page"),
    statusZoom: getRequired("editor-status-zoom"),
    statusSave: getRequired("editor-status-save"),
    // Structure counts in the statusbar.
    statusElemCount: getRequired("editor-status-elem-count"),
    statusIssueCount: getRequired("editor-status-issue-count"),
    statusSelCount: getRequired("editor-status-sel-count"),
    storageDot: getRequired("editor-storage-dot"),
    storageLabel: getRequired("editor-storage-label"),
    saveBtn: getRequired("editor-save-btn"),
    downloadBtn: getRequired("editor-download-btn"),
    undoBtn: getRequired("editor-undo-btn"),
    redoBtn: getRequired("editor-redo-btn"),
    helpBtn: getRequired("editor-help-btn"),
    openBtn: getRequired("editor-open-btn"),
    resetBtn: getRequired("editor-reset-btn"),
    // Hotkey legend modal.
    hotkeyModal: getRequired("editor-hotkey-modal"),
    hotkeyCloseBtn: getRequired("editor-hotkey-close-btn"),
    // Attestation pill + modal.
    attestationPill: getRequired("attestation-pill"),
    attestationModal: getRequired("attestation-modal"),
    attestationModalCloseBtn: getRequired("attestation-modal-close-btn"),
    attestationForm: getRequired("attestation-form"),
    attestationName: getRequired("attestation-name"),
    attestationEmail: getRequired("attestation-email"),
    attestationDate: getRequired("attestation-date"),
    attestationOrg: getRequired("attestation-org"),
    attestationNote: getRequired("attestation-note"),
    attestationRemember: getRequired("attestation-remember"),
    attestationError: getRequired("attestation-error"),
    attestationRevokeBtn: getRequired("attestation-revoke-btn"),
    attestationSubmitBtn: getRequired("attestation-submit-btn"),
    // Loading overlay (issue 6)
    loadingOverlay: getRequired("editor-loading-overlay"),
    loadingLabel: getRequired("editor-loading-label"),
    // Zoom (issue 7)
    zoomInBtn: getRequired("editor-zoom-in-btn"),
    zoomOutBtn: getRequired("editor-zoom-out-btn"),
    zoomDropdown: getRequired("editor-zoom-dropdown"),
    zoomCustomInput: getRequired("editor-zoom-custom-input"),
    zoomCustomGo: getRequired("editor-zoom-custom-go"),
    zoomControls: getRequired("editor-zoom-controls"),
    // Pane-header zoom dropdown — trigger button, label span, menu.
    paneZoomLevel: getRequired("editor-pane-zoom-level"),
    paneZoomLabel: getRequired("editor-pane-zoom-label"),
    paneZoomMenu: getRequired("editor-pane-zoom-menu"),
    fitPaneBtn: getRequired("editor-fit-pane-btn"),
    // Page nav (issue 8)
    pageNav: getRequired("editor-page-nav"),
    prevPageBtn: getRequired("editor-prev-page-btn"),
    nextPageBtn: getRequired("editor-next-page-btn"),
    pageInput: getRequired("editor-page-input"),
    toolbarPageTotal: getRequired("editor-toolbar-page-total"),
    // Inspector (was sidebar) view controls
    sbPrevPageBtn: getRequired("sb-prev-page-btn"),
    sbNextPageBtn: getRequired("sb-next-page-btn"),
    sbPageInput: getRequired("sb-page-input"),
    sbPageTotal: getRequired("sb-page-total"),
    sbZoomInBtn: getRequired("sb-zoom-in-btn"),
    sbZoomOutBtn: getRequired("sb-zoom-out-btn"),
};
export const state = {
    interop: null,
    structTree: null,
    pdfDoc: null,
    currentPage: 1,
    pageViewport: null,
    selectedStructId: null,
    fileName: null,
    // Current zoom scale (NaN = fit-to-width, a number = explicit).
    // fitToPaneMode: when true, zoom recomputes via fitToPageScale on resize.
    currentScale: NaN,
    fitToPaneMode: false,
    // IR + op log; the op log feeds the save flow (snapshotted on Save).
    ir: null,
    opLog: new OpLog(),
    // Current attestation (null = unattested).
    attestation: null,
    // Structure-overlay visibility. Default ON; user-toggleable via the PDF
    // pane toolbar; preference persisted in localStorage["editor:overlays"].
    overlaysVisible: (() => {
        try {
            const v = localStorage.getItem("editor:overlays"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            return v === null ? true : v !== "false";
        }
        catch {
            // localStorage unavailable (private mode / disabled) — default visible
            return true;
        }
    })(),
    // MCID-positions diagnostic overlay. Default OFF for ALL users
    // (Q2 in editor-mcid-overlay-260507.md). Persisted to
    // localStorage["editor:mcidOverlay"] (Q3: empty string === "false").
    mcidOverlayVisible: (() => {
        try {
            const v = localStorage.getItem("editor:mcidOverlay"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            return v === "true";
        }
        catch {
            return false;
        }
    })(),
    // structId → role lookup; rebuilt when state.structTree changes.
    overlayRoleMap: null,
    // ─── Card-GUI state ─────────────────────────────────────────────────────
    // Card list (one per top-level IR block) + expansion + selection.
    cards: [],
    expandedIds: new Set(),
    selectedStructIds: new Set(),
    // Map<structId, card> — overlays.js reads this to render alt-text tooltips.
    cardByStructId: new Map(),
    // Map<structId, 1-based index> — overlays.js draws the reading-order badge.
    readingOrderByStructId: new Map(),
    // Set<structId> — overlays.js renders the reading-order chip as "X, X+1"
    // for every structId in this set (their immediately-following IR block is
    // an AdaTool-stamped long-description paragraph). Rebuilt by
    // rebuildCardLookups whenever cards / IR change. Per
    // docs/design/long-description-policy-audit-260513.md §C.1.1.
    structIdsWithLongDescNext: new Set(),
    // Linked-views controller (selection mirroring + flash).
    linkedViewsCtrl: null,
    // Debounce / cancellation for overlay re-render on zoom.
    _overlayDebounceTimer: null,
    _overlayRenderSeq: 0,
    // Feedback FAB handle.
    feedbackFab: null,
    // Job id from URL ?job=... for /api/editor-ir/<jobId> fetch.
    jobId: null,
    // ─── Multi-page scroll + insert-flow + screen-reader ─────────────────────
    // MultiPageScrollManager instance; recreated each loadFile.
    multiPageMgr: null,
    // Array of per-page { frame, overlayEl, canvasEl } descriptors (1-based via [page-1]).
    pageDescriptors: [],
    // Per-page DragMoveController instances; key = 1-based page number.
    // Wired alongside DrawCreateController; dispatch on disjoint event
    // targets (drag-move = on `.editor-overlay-bbox`; draw = empty area).
    dragMoveControllers: new Map(),
    // Per-page ResizeMoveController instances. Wired on the same overlay as
    // DragMoveController; dispatch on `.editor-resize-handle` pointerdown.
    resizeMoveControllers: new Map(),
    // Per-page RubberBandController instances. Activated by shift-drag in
    // empty overlay space.
    rubberBandControllers: new Map(),
    // Per-page DrawCreateController instances. Handles left-click-drag in
    // empty overlay space: snap-to-MCID when mcids present, loose rect
    // fallback otherwise.
    drawCreateControllers: new Map(),
    // Contextmenu detach fns per page; key = 1-based page number. Wired in
    // _wirePageControllers to open the rich openContextMenu on per-page overlays.
    pageCtxDetachers: new Map(),
    // Single global PendingInsertStore for all pages.
    pendingInsertStore: new PendingInsertStore(),
    // ScreenReaderPanel instance; mounted once mountScreenReaderPanel() fires.
    srPanel: null,
    // ─── F7 IDB op-log (Stage 5; idb-oplog.js + session-store.js) ─────────
    // Lazy IDB handle for the v2 schema. Opened once in the boot block once
    // state.jobId is known. Null means "not yet opened" or "open failed";
    // every IDB call below gates on truthiness so the failure mode is
    // "no persistence" rather than "broken editor".
    editorDb: null,
    // ULID-ish id minted on every editor open (per memo §5.2). New session
    // every fresh tab, every back-button reload. Stable for the lifetime of
    // this in-memory state object.
    currentSessionId: generateSessionId(),
    // Cursor advanced on successful save: every op at or before
    // (sessionId, opSeq) has been server-acknowledged.
    lastSyncedCursor: { sessionId: null, opSeq: -1 },
    // Monotonic per-session opSeq for the ops appended to IDB this session.
    // Re-derived from loadLocalOps once the editor warms; until then we just
    // increment from -1.
    _idbLastOpSeq: -1,
    // Editor lifecycle state machine.
    // See web/static/editor/editor-lifecycle-state.js for the transition
    // table. The on_enter hook for LOADED_FULL fires window.__editorPdfReady
    // = true so smoke tests can wait for "PDF fully usable" without
    // polling the DOM. window.__editorReady (separate signal) still fires
    // at module-init for "module loaded; event listeners wired".
    lifecycle: new EditorStateMachine({
        onEnter: {
            [EditorLifecycleState.LOADED_FULL]: () => {
                window.__editorPdfReady = true; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            },
            [EditorLifecycleState.EMPTY_WARM]: () => {
                // Reset signals: clear the PDF-ready flag so smoke tests
                // that loop loadFile→reset→loadFile see the correct edge.
                window.__editorPdfReady = false; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            },
        },
    }),
    // Phase FB.8 derived index — rebuilt by the dispatcher (dispatcher.ts).
    // Initially empty; populated on first dispatch with non-doc-level dirty
    // pages, OR on initial IR load via `rebuildBlocksByPage(state.ir)`.
    blocksByPage: new Map(),
};
// ─── Per-page bbox cache (lazy-load redesign 260508) ────────────────────────
//
// Backs the page-1-paint hot path (~20-30 ms single-page WASM call vs
// ~hundreds of ms full-doc walk) and the post-Tier1 / viewport-vicinity
// prefetch chains. Implementation lives in bbox-cache.js so it's unit-
// testable without DOM bootstrapping; the editor binds it to `state.interop`
// via the lazy provider.
//
// Invalidation sites (predicates 2 and 3 of the original lazy-load design,
// extended by Phase FB.11 narrow invalidation):
//   - applyZoom() — overlays cache derived viewport coordinates that scale
//     with zoom; clear-on-zoom is the conservative choice (full clear).
//   - resetEditorState() — new PDF means new struct tree (full clear).
//   - dispatcher.commitResult() — geometry-shaped ops (set-bbox /
//     create-element / delete-element / reorder-reading) narrow-evict
//     only `result.dirtyPages` via `_bboxCache.invalidatePages(...)`;
//     metadata ops leave the cache intact. See dispatcher.ts
//     `shouldInvalidateBboxCache`.
//   - MultiPageScrollManager.demote — viewport-exit hook narrow-evicts
//     the demoted page so the cache no longer grows monotonically. See
//     editor-multipage.ts.
//
// The Phase FB.11 demote + dispatcher hooks together close the
// "monotonically grows until invalidated" gap noted in the original
// design (docs/design/editor-lazy-load-redesign-260508.md §6 + §11.2;
// docs/design/frontend-mv-architecture-260524.md §P.4 / §P.7).
//
// Lives in editor-state.ts (not bbox-cache.ts) because it is referenced by
// both Cluster B (resetEditorState) and Cluster D (render/applyZoom) —
// promoting the singleton here avoids a cross-cluster dependency.
export const _bboxCache = createBboxCache(() => state.interop);
// Aliases that keep the call sites readable AND match the names in the
// design doc's pseudocode (so a future arch-audit can `git grep` them).
export const getBoundingBoxesForPageCached = async (pageNum) => _bboxCache.getForPage(pageNum);
export const prefetchRange = async (start, end) => _bboxCache.prefetchRange(start, end);
// ─── Selection mutator ────────────────────────────────────────────────────
//
// Single source of truth for editor selection state. Always call this
// instead of writing state.selectedStructId / state.selectedStructIds
// directly — the two fields must stay in sync so overlays, cards, and
// the inspector all reflect the same selection.
//
// idOrSet may be:
//   - a string structId          → single-select
//   - a Set<string>              → multi-select
//   - null / undefined / empty Set → clear
//
export function setEditorSelection(idOrSet) {
    // Delegate the pure computation to the seam module (editor-state-logic.ts)
    // so the algorithm is independently unit-testable without DOM bootstrapping.
    const result = computeSelectionState(idOrSet);
    state.selectedStructIds = result.selectedStructIds;
    state.selectedStructId = result.selectedStructId;
}
// ─── Dirty predicate — the SINGLE queryable seam for "has unsaved edits" ────
//
// One source of truth for the Open dirty-guard (§G-Q1: confirm before an
// irreversible reset discards unsaved work) and any future same-document
// reload guard (§G-Q2) — so both read ONE predicate rather than re-deriving
// "dirty" independently (→ A.24 second-surface). "Dirty" = at least one applied
// op on the undo stack OR at least one unresolved editor-local pending insert.
// Epic editor-open-load-state-coherence-260813 Phase-1b §G-Q1.
export function isEditorDirty() {
    return state.opLog.canUndo() || state.pendingInsertStore.size() > 0;
}
// ─── Reset-completeness dev-assertion (§G-Q6(c)) ────────────────────────────
//
// Fail-loud guard that resetEditorState() cleared every doc-derived field it
// owns. loadDocument calls this immediately after resetEditorState() on a
// document switch: if a future edit adds a `state.<docfield>` and forgets to
// clear it in resetEditorState, the NEXT Open surfaces the omission loudly here
// rather than silently rendering the prior document's data (the BUG #5 class,
// per-field). This is the runtime dev-assertion form of the reset-completeness
// check; the marker-based structural lint (design §4 option (a)) is deferred to
// Phase 4 (rule #55 AUDIT-ONLY-first), and the `state.doc` unification (option
// (b)) is out of scope here (shared-fate with the ground-truth Epic). Only
// fields resetEditorState is RESPONSIBLE for are checked — state.opLog is reset
// late in loadFile's Tier-2, not by resetEditorState, so it is excluded.
export function assertDocStateCleared() {
    const residual = [];
    if (state.jobId !== null)
        residual.push("jobId");
    if (state.interop !== null)
        residual.push("interop");
    if (state.ir !== null)
        residual.push("ir");
    if (state.structTree !== null)
        residual.push("structTree");
    if (state.attestation !== null)
        residual.push("attestation");
    if (state.cards.length !== 0)
        residual.push("cards");
    if (state.selectedStructIds.size !== 0)
        residual.push("selectedStructIds");
    if (state.expandedIds.size !== 0)
        residual.push("expandedIds");
    if (state.pendingInsertStore.size() !== 0)
        residual.push("pendingInsertStore");
    if (residual.length > 0) {
        throw new Error(`[editor] resetEditorState left doc-derived state uncleared: ${residual.join(", ")}. `
            + `Every doc-derived state.* field must be reset in resetEditorState so a document `
            + `switch cannot render prior-document data (BUG #5 class).`);
    }
}
